<script setup lang="ts">
import type { ConfiguratorInitOptions } from '@/RavakConfigurator';

import { VCodeBlock } from '@wdns/vue-code-block';
import { format } from 'prettier';
import * as prettierPluginBabel from 'prettier/plugins/babel';
import { default as prettierPluginEstree } from 'prettier/plugins/estree';
import * as prettierPluginHtml from 'prettier/plugins/html';
import * as prettierPluginCss from 'prettier/plugins/postcss';
import stringifyObject from 'stringify-object';
import {
  computed,
  type CSSProperties,
  ref,
  type StyleValue,
  useTemplateRef,
  watchEffect,
} from 'vue';

const props = defineProps<{
  isDevelopment: boolean;
}>();

enum Configurator {
  Ravak = 'RavakConfigurator',
  Universal = 'UniversalConfigurator',
}

enum ConfiguratorIdentifier {
  Bathtubs = 'bathtubs-sets',
  Dummy = 'dummy-configurator',
  Lite = 'lite-sets',
  Yard = 'yard-sets',
}

enum Consumer {
  Dummy = 'dummy-consumer',
  None = '',
  RavakCzechia = 'ravak-cz-dev',
  RavakGlobal = 'ravak-com-dev',
  RavakHungary = 'ravak-hu-dev',
  RavakLithuania = 'ravak-lt-dev',
  RavakPoland = 'ravak-pl-dev',
  RavakSpain = 'ravak-es-dev',
}

enum Language {
  Bulgarian = 'bg',
  Czech = 'cs',
  English = 'en',
  Estonian = 'et',
  German = 'de',
  Hungarian = 'hu',
  Latvian = 'lv',
  Lithuanian = 'lt',
  Polish = 'pl',
  Slovak = 'sk',
  Spanish = 'es',
  Ukrainian = 'uk',
  Unspecified = '',
}

const params = new URLSearchParams(window.location.search);

const configurator = ((props.isDevelopment && params.get('configurator')) ||
  Configurator.Ravak) as Configurator;

const configuratorContainer = useTemplateRef('container');

const identifier = (params.get('identifier') ??
  ConfiguratorIdentifier.Yard) as ConfiguratorIdentifier;

const preset = params.get('preset');

const simulateBasketCallback = params.has('basket');

const treatCheckoutAsMerchantQuestion = params.has('merchant-question');

const initOptions = computed<ConfiguratorInitOptions>(() => {
  const options: ConfiguratorInitOptions = { consumer, language };

  if (preset) options.preset = preset;

  if (!passAppearanceUsingCssVariables.value) {
    if (!renderInContainer.value && passContainerWidthToConfigurator.value) {
      options.containerWidth = containerWidth.value;
    }

    if (setHeightOffset.value) {
      options.heightOffset = heightOffset.value;
    }

    if (showStickyHeader.value) {
      options.topOffset = stickyHeaderHeight.value;
    }
  }

  if (treatCheckoutAsMerchantQuestion) {
    options.treatCheckoutAsMerchantQuestion = true;
  }

  if (simulateBasketCallback) {
    options.checkoutPrimaryActionButtonLabelConfigurationSummary = 'Add to basket';
    options.checkoutItemAmountsCallback = () => {
      alert('Added to basket');
      return false;
    };
  }

  return options;
});

const consumer = (params.get('consumer') ?? Consumer.RavakCzechia) as Consumer;

const language = (params.get('language') ?? Language.Czech) as Language;

/**
 * Dependent properties (e.g. `passContainerWidthToConfigurator` and `containerWidth`) must be
 * accessed before first `await` in order for `watchEffect` to track them.
 */
watchEffect(async () => {
  if (!configuratorContainer.value) return;
  const options = {
    ...initOptions.value,
  };

  if (configurator === Configurator.Ravak) {
    const { default: RavakConfigurator } = await import('@/RavakConfigurator');

    RavakConfigurator.init(configuratorContainer.value, identifier, options);
  }

  if (configurator === Configurator.Universal) {
    const { default: UniversalConfigurator } = await import('@/UniversalConfigurator');

    UniversalConfigurator.init(configuratorContainer.value, {
      ...options,
      apiUrl: import.meta.env.VITE_API_URL,
      identifier,
    });
  }
});

const labelStyle: StyleValue = {
  display: 'grid',
  gridColumn: '1 / -1',
  gridTemplateColumns: 'subgrid',
};

const initializationCode = ref('');

const containerWidth = ref('1500px');
const renderInContainer = ref(false);
const passContainerWidthToConfigurator = ref(true);

const setHeightOffset = ref(true);
const heightOffset = ref('68px');

const showStickyHeader = ref(false);
const stickyHeaderHeight = ref('100px');

const passAppearanceUsingCssVariables = ref(false);

const formGridStyle: StyleValue = {
  alignContent: 'start',
  display: 'grid',
  flexDirection: 'column',
  gap: '1em',
  gridTemplateColumns: 'repeat(2, auto)',
  padding: '1em',
};

const mountElementStyle = computed<CSSProperties>(() => {
  const properties: CSSProperties = {};
  if (!passAppearanceUsingCssVariables.value) return properties;

  if (!renderInContainer.value && passContainerWidthToConfigurator.value) {
    properties['--configurator-container-width'] = containerWidth.value;
  }

  if (setHeightOffset.value) {
    properties['--configurator-height-offset'] = heightOffset.value;
  }

  if (showStickyHeader.value) {
    properties['--configurator-top-offset'] = stickyHeaderHeight.value;
  }

  return properties;
});

const containerStyle = computed<StyleValue>(() => ({
  background: 'white',
  boxShadow: '0 0 1em rgba(0, 0, 0, 0.1)',
  boxSizing: 'border-box',
  marginInline: 'auto',
  maxWidth: containerWidth.value,
  overflow: 'hidden',
  paddingInline: '1em',
}));

watchEffect(async () => {
  const mountElementId = 'configurator-container';

  initializationCode.value = await format(
    `<div id="${mountElementId}" ${
      Object.values(mountElementStyle.value).length > 0
        ? `style="${Object.entries(mountElementStyle.value)
            .map(([k, v]) => `${k}: ${v}`)
            .join('; ')}"`
        : ''
    }></div>
    <!-- ... -->
    <script>
      window.onload = () => {
        const script = document.createElement('script');
        script.src = 'RavakConfigurator.js';
        script.defer = true;

        script.onload = () =>
          RavakConfigurator.init('#${mountElementId}', '${identifier}', ${
            Object.keys(initOptions.value).length > 0 ? stringifyObject(initOptions.value) : ''
          });

        document.body.appendChild(script);
      };
    <\/script>`,
    {
      parser: 'html',
      plugins: [prettierPluginBabel, prettierPluginEstree, prettierPluginHtml, prettierPluginCss],
    },
  );
});
</script>

<template>
  <div class="debug-view">
    <div
      v-if="showStickyHeader"
      :style="{
        position: 'fixed',
        height: stickyHeaderHeight,
        width: '100%',
        background: '#4444',
        zIndex: 1000,
        pointerEvents: 'none',
      }"
    />
    <div class="container" :style="containerStyle">
      <h1>Ravak Configurator</h1>
      <form>
        <div :style="{ display: 'flex', flexWrap: 'wrap', gap: '1em', marginBlockEnd: '1em' }">
          <fieldset :style="formGridStyle">
            <legend><h2>Page setup</h2></legend>

            <label :style="labelStyle">
              Container width:
              <input type="text" v-model.lazy="containerWidth" />
            </label>

            <label :style="{ ...labelStyle, display: 'block' }">
              <input type="checkbox" v-model="renderInContainer" />

              Render in container
            </label>

            <label v-if="!renderInContainer" :style="{ ...labelStyle, display: 'block' }">
              <input type="checkbox" v-model="passContainerWidthToConfigurator" />

              Pass container width to configurator
            </label>

            <hr :style="{ ...labelStyle, display: 'block', width: '100%', margin: 0 }" />

            <label :style="{ ...labelStyle, display: 'block' }">
              <input type="checkbox" v-model="setHeightOffset" />

              Set height offset
            </label>

            <label :style="labelStyle" v-if="setHeightOffset">
              Height offset:
              <input type="text" v-model.lazy="heightOffset" />
            </label>

            <hr :style="{ ...labelStyle, display: 'block', width: '100%', margin: 0 }" />

            <label :style="{ ...labelStyle, display: 'block' }">
              <input type="checkbox" v-model="showStickyHeader" />

              Show sticky header
            </label>

            <label :style="labelStyle" v-if="showStickyHeader">
              Sticky header height:
              <input type="text" v-model="stickyHeaderHeight" />
            </label>
          </fieldset>

          <fieldset :style="formGridStyle">
            <legend><h2>Configuration</h2></legend>

            <label :style="labelStyle" v-if="isDevelopment">
              Configurator:
              <select name="configurator">
                <option
                  v-for="[name, value] in Object.entries(Configurator)"
                  :key="value"
                  :value="value"
                  v-text="name"
                  :selected="value === configurator"
                />
              </select>
            </label>

            <label :style="labelStyle">
              Identifier:
              <select name="identifier">
                <template v-for="[name, value] in Object.entries(ConfiguratorIdentifier)">
                  <option
                    v-if="isDevelopment || value !== ConfiguratorIdentifier.Dummy"
                    :key="value"
                    :value="value"
                    v-text="name"
                    :selected="value === identifier"
                  />
                </template>
              </select>
            </label>

            <label :style="labelStyle">
              Preset:
              <input :value="preset" name="preset" />
            </label>

            <label :style="labelStyle">
              Consumer:
              <select name="consumer">
                <template v-for="[name, value] in Object.entries(Consumer)">
                  <option
                    v-if="isDevelopment || value !== Consumer.Dummy"
                    :key="value"
                    :value="value"
                    v-text="name"
                    :selected="value === consumer"
                  />
                </template>
              </select>
            </label>

            <label :style="labelStyle">
              Language:
              <select name="language">
                <template v-for="[name, value] in Object.entries(Language)">
                  <option
                    v-if="isDevelopment || value !== Language.Unspecified"
                    :key="value"
                    :value="value"
                    v-text="name"
                    :selected="value === language"
                  />
                </template>
              </select>
            </label>

            <label :style="{ ...labelStyle, display: 'block' }">
              <input type="checkbox" v-model="passAppearanceUsingCssVariables" />

              Pass appearance using CSS variables
            </label>

            <label :style="{ ...labelStyle, display: 'block' }">
              <input type="checkbox" name="basket" :checked="simulateBasketCallback" />

              Simulate basket callback
            </label>

            <label :style="{ ...labelStyle, display: 'block' }">
              <input
                type="checkbox"
                name="merchant-question"
                :checked="treatCheckoutAsMerchantQuestion"
              />

              Treat checkout as merchant question
            </label>

            <input :style="{ gridColumn: '1 / -1' }" type="submit" value="Apply" />
          </fieldset>

          <div :style="{ overflowY: 'auto' }">
            <fieldset>
              <legend><h2>Initialization</h2></legend>

              <VCodeBlock prismjs theme="default" lang="html" :code="initializationCode" />
            </fieldset>
          </div>
        </div>
      </form>

      <h2>Preview</h2>

      <Teleport defer to="#target-out-container" :disabled="renderInContainer">
        <div
          ref="container"
          :style="{ ...mountElementStyle, ...(renderInContainer ? { marginInline: '-1em' } : {}) }"
        />
      </Teleport>
    </div>

    <div id="target-out-container" />

    <div class="container" :style="containerStyle">
      <p style="width: 300px">
        Nullam massa leo, bibendum at varius sit amet, sodales id risus. Mauris turpis leo, interdum
        non consectetur eu, iaculis eu urna. Class aptent taciti sociosqu ad litora torquent per
        conubia nostra, per inceptos himenaeos. Donec vestibulum imperdiet vehicula. Pellentesque
        habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Nullam
        massa leo, bibendum at varius sit amet, sodales id risus. Mauris turpis leo, interdum non
        consectetur eu, iaculis eu urna. Class aptent taciti sociosqu ad litora torquent per conubia
        nostra, per inceptos himenaeos. Donec vestibulum imperdiet vehicula. Pellentesque habitant
        morbi tristique senectus et netus et malesuada fames ac turpis egestas.
      </p>
    </div>
  </div>
</template>
